import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';

const NAME_MIN_LENGTH = 5;
const NAME_MAX_LENGTH = 100;
const DESCRIPTION_MAX_LENGTH = 250;

interface ConsentTemplate {
  id?: string;
  name: string;
  description?: string;
  content: string;
  form_type: 'telehealth' | 'hipaa' | 'financial' | 'treatment' | 'custom';
  is_required_for_booking: boolean;
  is_required_for_telehealth: boolean;
  signature_required: boolean;
  expires_after_days?: number;
}

interface ConsentFormEditorProps {
  template?: ConsentTemplate | null;
  onSave: (data: Partial<ConsentTemplate>) => Promise<void>;
  onCancel: () => void;
}

const ConsentFormEditor: React.FC<ConsentFormEditorProps> = ({
  template,
  onSave,
  onCancel,
}) => {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<Partial<ConsentTemplate>>({
    name: '',
    description: '',
    content: '',
    form_type: 'custom',
    is_required_for_booking: false,
    is_required_for_telehealth: false,
    signature_required: true,
    expires_after_days: undefined,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name,
        description: template.description || '',
        content: template.content,
        form_type: template.form_type,
        is_required_for_booking: template.is_required_for_booking,
        is_required_for_telehealth: template.is_required_for_telehealth,
        signature_required: template.signature_required,
        expires_after_days: template.expires_after_days,
      });
    }
  }, [template]);

  const handleChange = (field: keyof ConsentTemplate, value: any) => {
    // Enforce max length limits
    if (field === 'name' && typeof value === 'string' && value.length > NAME_MAX_LENGTH) {
      return; // Don't update if exceeds max length
    }
    if (field === 'description' && typeof value === 'string' && value.length > DESCRIPTION_MAX_LENGTH) {
      return; // Don't update if exceeds max length
    }
    
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when field is updated
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate name
    const nameTrimmed = formData.name?.trim() || '';
    if (!nameTrimmed) {
      newErrors.name = 'Name is required';
    } else if (nameTrimmed.length < NAME_MIN_LENGTH) {
      newErrors.name = `Name must be at least ${NAME_MIN_LENGTH} characters`;
    } else if (nameTrimmed.length > NAME_MAX_LENGTH) {
      newErrors.name = `Name must not exceed ${NAME_MAX_LENGTH} characters`;
    }

    // Validate description
    if (formData.description && formData.description.length > DESCRIPTION_MAX_LENGTH) {
      newErrors.description = `Description must not exceed ${DESCRIPTION_MAX_LENGTH} characters`;
    }

    // Validate content
    if (!formData.content?.trim()) {
      newErrors.content = 'Content is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      await onSave(formData);
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Info */}
      <div className="space-y-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="name">
              Template Name <span className="text-destructive">*</span>
            </Label>
            <span
              className={`text-xs ${
                (formData.name?.length || 0) > NAME_MAX_LENGTH - 10
                  ? 'text-amber-600'
                  : 'text-muted-foreground'
              } ${
                (formData.name?.length || 0) > NAME_MAX_LENGTH
                  ? 'text-destructive font-medium'
                  : ''
              }`}
            >
              {(formData.name?.length || 0)}/{NAME_MAX_LENGTH}
            </span>
          </div>
          <Input
            id="name"
            value={formData.name || ''}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="e.g., Informed Consent for Telehealth Services"
            maxLength={NAME_MAX_LENGTH}
            className={errors.name ? 'border-destructive' : ''}
          />
          {errors.name && (
            <p className="text-sm text-destructive">{errors.name}</p>
          )}
          {!errors.name && (formData.name?.length || 0) < NAME_MIN_LENGTH && formData.name && (
            <p className="text-xs text-muted-foreground">
              Minimum {NAME_MIN_LENGTH} characters required
            </p>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="description">Description</Label>
            <span
              className={`text-xs ${
                (formData.description?.length || 0) > DESCRIPTION_MAX_LENGTH - 20
                  ? 'text-amber-600'
                  : 'text-muted-foreground'
              } ${
                (formData.description?.length || 0) > DESCRIPTION_MAX_LENGTH
                  ? 'text-destructive font-medium'
                  : ''
              }`}
            >
              {(formData.description?.length || 0)}/{DESCRIPTION_MAX_LENGTH}
            </span>
          </div>
          <Input
            id="description"
            value={formData.description || ''}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Brief description of this consent form"
            maxLength={DESCRIPTION_MAX_LENGTH}
            className={errors.description ? 'border-destructive' : ''}
          />
          {errors.description && (
            <p className="text-sm text-destructive">{errors.description}</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="form_type">Form Type</Label>
            <Select
              value={formData.form_type}
              onValueChange={(value) => handleChange('form_type', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="telehealth">Telehealth Consent</SelectItem>
                <SelectItem value="hipaa">HIPAA Authorization</SelectItem>
                <SelectItem value="financial">Financial Responsibility</SelectItem>
                <SelectItem value="treatment">Treatment Consent</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="expires_after_days">Expires After (Days)</Label>
            <Input
              id="expires_after_days"
              type="number"
              min="0"
              value={formData.expires_after_days || ''}
              onChange={(e) =>
                handleChange(
                  'expires_after_days',
                  e.target.value ? parseInt(e.target.value, 10) : undefined
                )
              }
              placeholder="Never expires if blank"
            />
          </div>
        </div>
      </div>

      {/* Content Editor */}
      <div className="space-y-2">
        <Label htmlFor="content">
          Consent Form Content <span className="text-destructive">*</span>
        </Label>
        <p className="text-sm text-muted-foreground">
          Use HTML tags for formatting. Supports headings (h2, h3), paragraphs (p),
          lists (ul, ol, li), and emphasis (strong, em).
        </p>
        <Textarea
          id="content"
          value={formData.content || ''}
          onChange={(e) => handleChange('content', e.target.value)}
          placeholder="<h2>Consent Title</h2>
<p>Consent content goes here...</p>
<ul>
  <li>Point 1</li>
  <li>Point 2</li>
</ul>"
          className={`min-h-[300px] font-mono text-sm ${
            errors.content ? 'border-destructive' : ''
          }`}
        />
        {errors.content && (
          <p className="text-sm text-destructive">{errors.content}</p>
        )}
      </div>

      {/* Requirements */}
      <div className="space-y-4">
        <h4 className="font-medium">Requirements</h4>
        
        <div className="flex items-center justify-between py-3 px-4 bg-muted/50 rounded-lg">
          <div>
            <Label htmlFor="signature_required" className="font-medium">
              Signature Required
            </Label>
            <p className="text-sm text-muted-foreground">
              Patient must provide a signature to complete this consent
            </p>
          </div>
          <Switch
            id="signature_required"
            checked={formData.signature_required}
            onCheckedChange={(checked) => handleChange('signature_required', checked)}
          />
        </div>

        <div className="flex items-center justify-between py-3 px-4 bg-muted/50 rounded-lg">
          <div>
            <Label htmlFor="is_required_for_booking" className="font-medium">
              Required for Booking
            </Label>
            <p className="text-sm text-muted-foreground">
              Patient must sign before booking an appointment
            </p>
          </div>
          <Switch
            id="is_required_for_booking"
            checked={formData.is_required_for_booking}
            onCheckedChange={(checked) =>
              handleChange('is_required_for_booking', checked)
            }
          />
        </div>

        <div className="flex items-center justify-between py-3 px-4 bg-muted/50 rounded-lg">
          <div>
            <Label htmlFor="is_required_for_telehealth" className="font-medium">
              Required for Telehealth
            </Label>
            <p className="text-sm text-muted-foreground">
              Patient must sign before joining a telehealth call
            </p>
          </div>
          <Switch
            id="is_required_for_telehealth"
            checked={formData.is_required_for_telehealth}
            onCheckedChange={(checked) =>
              handleChange('is_required_for_telehealth', checked)
            }
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={saving}>
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {template ? 'Save Changes' : 'Create Template'}
        </Button>
      </div>
    </form>
  );
};

export default ConsentFormEditor;










